import { useState, type FormEvent } from "react";
import { api } from "../api";

interface Props {
  onAuthenticated: () => Promise<void>;
  onBack: () => void;
}

function friendlyError(message: string): string {
  if (message === "invalid_credentials") return "Неверная почта или пароль.";
  if (message === "invalid_or_expired_token") return "Ссылка недействительна или уже использована.";
  return "Не получилось выполнить действие. Попробуйте ещё раз.";
}

export function AuthScreen({ onAuthenticated, onBack }: Props) {
  const resetToken = new URLSearchParams(window.location.search).get("token");
  const [mode, setMode] = useState<"login" | "forgot" | "reset">(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (mode === "login") {
        await api.login(email, password);
        await onAuthenticated();
      } else if (mode === "forgot") {
        const result = await api.forgotPassword(email);
        setMessage(result.developmentResetUrl
          ? `Ссылка для разработки: ${result.developmentResetUrl}`
          : "Если аккаунт существует, письмо со ссылкой уже отправлено.");
      } else if (resetToken) {
        await api.resetPassword(resetToken, password);
        window.history.replaceState({}, "", "/login");
        setMode("login");
        setMessage("Пароль изменён. Теперь можно войти.");
        setPassword("");
      }
    } catch (error) {
      setMessage(friendlyError(error instanceof Error ? error.message : ""));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="brand-panel">
        <button className="auth-back" onClick={onBack}>← На главную</button>
        <p className="site-kicker">PETROSETR.COM</p>
        <h1>Личный кабинет петра осетра.</h1>
        <p className="brand-copy">Дневник, избранные записи и личные проекты в одном спокойном пространстве.</p>
        <p className="auth-quote">Private by default.<br />Public by choice.</p>
      </section>
      <section className="auth-card" aria-labelledby="auth-title">
        <p className="site-kicker">ДОБРО ПОЖАЛОВАТЬ</p>
        <h2 id="auth-title">{mode === "forgot" ? "Восстановить пароль" : mode === "reset" ? "Новый пароль" : "Войти"}</h2>
        <p className="muted">{mode === "login"
          ? "Вход только для владельца сайта. Используйте почту владельца и свой пароль."
          : "Данные дневника сохраняются на сервере."}</p>
        <form onSubmit={submit}>
          {mode !== "reset" && <label>Почта<input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>}
          {mode !== "forgot" && <label>Пароль<input type="password" autoComplete={mode === "reset" ? "new-password" : "current-password"} placeholder={mode === "reset" ? "Придумайте пароль" : "Ваш пароль"} value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required /><small>Не менее 10 символов</small></label>}
          <button className="primary-button" disabled={busy}>{busy ? "Подождите…" : mode === "forgot" ? "Отправить ссылку" : mode === "reset" ? "Сохранить пароль" : "Войти"}</button>
        </form>
        {message && <p className="form-message" role="status">{message}</p>}
        <div className="auth-links">
          {mode === "login" && <button onClick={() => setMode("forgot")}>Забыли пароль?</button>}
          {mode !== "login" && <button onClick={() => setMode("login")}>Вернуться ко входу</button>}
        </div>
      </section>
    </main>
  );
}
